import { __decorate } from "tslib";
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import raf from 'raf';
import { observer } from 'mobx-react';
import omit from 'lodash/omit';
import isNumber from 'lodash/isNumber';
import isUndefined from 'lodash/isUndefined';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import classes from 'component-classes';
import { action, get, toJS } from 'mobx';
import { DragDropContext, } from 'react-beautiful-dnd';
import { getConfig } from '../../../lib/configure';
import warning from '../../../lib/_util/warning';
import { pxToRem, toPx } from '../../../lib/_util/UnitConvertor';
import measureScrollbar from '../../../lib/_util/measureScrollbar';
import KeyCode from '../../../lib/_util/KeyCode';
import ReactResizeObserver from '../../../lib/_util/resizeObserver';
import Column, { defaultMinWidth } from './Column';
import TableRow from './TableRow';
import TableHeaderCell from './TableHeaderCell';
import TableStore from './TableStore';
import TableHeader from './TableHeader';
import autobind from '../_util/autobind';
import Pagination from '../pagination/Pagination';
import Spin from '../spin';
import DataSetComponent from '../data-set/DataSetComponent';
import TableContext from './TableContext';
import TableWrapper from './TableWrapper';
import TableTBody from './TableTBody';
import TableFooter from './TableFooter';
import { ColumnLock, DragColumnAlign, HighLightRowType, ScrollPosition, SelectionMode, TableAutoHeightType, TableButtonType, TableEditMode, TableHeightType, TableMode, TablePaginationPosition, TableQueryBarType, } from './enum';
import TableQueryBar from './query-bar';
import ToolBar from './query-bar/TableToolBar';
import FilterBar from './query-bar/TableFilterBar';
import AdvancedQueryBar from './query-bar/TableAdvancedQueryBar';
import ProfessionalBar from './query-bar/TableProfessionalBar';
import DynamicFilterBar from './query-bar/TableDynamicFilterBar';
import { findIndexedSibling, getHeight, getPaginationPosition, isCanEdictingRow, isDropresult, isStickySupport } from './utils';
import TableBody from './TableBody';
import VirtualWrapper from './VirtualWrapper';
import ModalProvider from '../modal-provider/ModalProvider';
import SelectionTips from './SelectionTips';
import { DataSetSelection } from '../data-set/enum';
export const buttonsEnumType = PropTypes.oneOf([
    TableButtonType.add,
    TableButtonType.save,
    TableButtonType.remove,
    TableButtonType.delete,
    TableButtonType.reset,
    TableButtonType.query,
    TableButtonType.export,
    TableButtonType.expandAll,
    TableButtonType.collapseAll,
]);
let _instance;
// 构造一个单例table来防止body下不能有table元素的报错
export const instance = (wrapperClassName, prefixCls) => {
    // Using a table as the portal so that we do not get react
    // warnings when mounting a tr element
    const _tableContain = () => {
        const table = document.createElement('table');
        table.className = wrapperClassName;
        const thead = document.createElement('thead');
        thead.className = `${prefixCls}-thead`;
        table.appendChild(thead);
        const headtr = document.createElement('tr');
        thead.appendChild(headtr);
        const tbody = document.createElement('tbody');
        tbody.className = `${prefixCls}-tbody`;
        table.appendChild(tbody);
        if (!document.body) {
            throw new Error('document.body required a body to append');
        }
        document.body.appendChild(table);
        return {
            // @ts-ignore
            tbody,
            // @ts-ignore
            headtr,
        };
    };
    if (_instance) {
        return _instance;
    }
    return _instance = _tableContain();
};
let Table = class Table extends DataSetComponent {
    constructor() {
        super(...arguments);
        this.tableStore = new TableStore(this);
        this.wrapperWidth = [];
        /**
         * 滚动结束隐藏spin
         */
        this.setSpin = debounce(() => {
            const { refSpin } = this;
            if (refSpin) {
                refSpin.style.display = 'none';
            }
        }, 300);
    }
    get currentRow() {
        const { tableStore: { prefixCls } } = this;
        return this.element.querySelector(`.${prefixCls}-row-current`);
    }
    get firstRow() {
        const { tableStore: { prefixCls } } = this;
        return this.element.querySelector(`.${prefixCls}-row:first-child`);
    }
    get lastRow() {
        const { tableStore: { prefixCls } } = this;
        return this.element.querySelector(`.${prefixCls}-row:last-child`);
    }
    saveVirtualSpinRef(node) {
        this.refSpin = node;
    }
    saveResizeRef(node) {
        this.resizeLine = node;
    }
    saveTableHeader(tableHeader) {
        this.tableHeader = tableHeader;
    }
    saveTableFooter(tableFooter) {
        this.tableFooter = tableFooter;
    }
    useFocusedClassName() {
        return false;
    }
    handleResize(width) {
        const { element, tableStore, wrapperWidth } = this;
        if (width !== undefined) {
            const duplicate = wrapperWidth.includes(width);
            wrapperWidth.unshift(width);
            window.clearTimeout(this.wrapperWidthTimer);
            this.wrapperWidthTimer = window.setTimeout(() => {
                wrapperWidth.pop();
            }, 500);
            if (wrapperWidth.length > 2) {
                wrapperWidth.pop();
            }
            if (duplicate) {
                return;
            }
        }
        if (!element.offsetParent) {
            tableStore.styledHidden = true;
        }
        else if (!tableStore.hidden) {
            this.syncSizeInFrame(width);
        }
        else {
            tableStore.styledHidden = false;
        }
    }
    handleWindowResize() {
        this.handleResize();
    }
    handleDataSetLoad() {
        this.initDefaultExpandedRows();
    }
    handleDataSetCreate({ record, dataSet }) {
        const { tableStore } = this;
        if (tableStore.inlineEdit) {
            if (tableStore.currentEditRecord) {
                tableStore.currentEditRecord.reset();
                dataSet.remove(record);
            }
            else {
                tableStore.currentEditRecord = record;
            }
        }
    }
    handleKeyDown(e) {
        const { tableStore } = this;
        const { keyboard } = tableStore;
        if (!tableStore.editing) {
            try {
                const { dataSet } = this.props;
                const ctrlKey = e.ctrlKey || e.metaKey;
                const altKey = e.altKey;
                const shiftKey = e.shiftKey;
                switch (e.keyCode) {
                    case KeyCode.UP:
                        if (shiftKey && keyboard) {
                            this.handleKeyDownUpShift(e);
                        }
                        else {
                            this.handleKeyDownUp(e);
                        }
                        break;
                    case KeyCode.DOWN:
                        if (shiftKey && keyboard) {
                            this.handleKeyDownDownShift(e);
                        }
                        else {
                            this.handleKeyDownDown(e);
                        }
                        break;
                    case KeyCode.RIGHT:
                        this.handleKeyDownRight(e);
                        break;
                    case KeyCode.LEFT:
                        this.handleKeyDownLeft(e);
                        break;
                    case KeyCode.PAGE_UP:
                        e.preventDefault();
                        dataSet.prePage();
                        break;
                    case KeyCode.PAGE_DOWN:
                        e.preventDefault();
                        dataSet.nextPage();
                        break;
                    case KeyCode.HOME:
                        this.handleKeyDownHome(e);
                        break;
                    case KeyCode.END:
                        this.handleKeyDownEnd(e);
                        break;
                    case KeyCode.S:
                        if (ctrlKey === true && keyboard)
                            this.handleKeyDownCTRLS(e);
                        break;
                    case KeyCode.N:
                        if (altKey === true && keyboard)
                            this.handleKeyDownCTRLN(e);
                        break;
                    case KeyCode.D:
                        if (ctrlKey === true && keyboard)
                            this.handleKeyDownCTRLD(e);
                        break;
                    case KeyCode.DELETE:
                        if (altKey === true && keyboard)
                            this.handleKeyDownCTRLDELETE(e);
                        break;
                    default:
                }
            }
            catch (error) {
                warning(false, error.message);
            }
        }
        const { onKeyDown = noop } = this.props;
        onKeyDown(e);
    }
    focusRow(row) {
        if (row) {
            const { index } = row.dataset;
            if (index) {
                const { dataSet } = this.props;
                const record = dataSet.findRecordById(index);
                if (record) {
                    dataSet.current = record;
                    return record;
                }
            }
        }
    }
    async handleKeyDownHome(e) {
        e.preventDefault();
        const { dataSet } = this.props;
        if (!this.tableStore.isTree) {
            await dataSet.first();
        }
        this.focusRow(this.firstRow);
    }
    async handleKeyDownEnd(e) {
        e.preventDefault();
        const { dataSet } = this.props;
        if (!this.tableStore.isTree) {
            await dataSet.last();
        }
        this.focusRow(this.lastRow);
    }
    async handleKeyDownCTRLS(e) {
        e.preventDefault();
        const { tableStore: { dataSet }, } = this;
        dataSet.submit();
    }
    async handleKeyDownCTRLN(e) {
        e.preventDefault();
        const { tableStore: { dataSet }, } = this;
        dataSet.create({}, 0);
    }
    async handleKeyDownCTRLD(e) {
        e.preventDefault();
        const { currentRow, tableStore } = this;
        const { dataSet } = tableStore;
        let currentElementSibling = null;
        // to justice it can be change or standards compliant
        if (isCanEdictingRow(currentRow)) {
            currentElementSibling = currentRow;
        }
        if (currentElementSibling && tableStore && dataSet) {
            const previousElementSibling = findIndexedSibling(currentElementSibling, -1);
            if (previousElementSibling) {
                const { index } = previousElementSibling.dataset;
                const { index: currentIndex } = currentElementSibling.dataset;
                if (index && currentIndex) {
                    const record = dataSet.findRecordById(index);
                    const currentRecord = dataSet.findRecordById(currentIndex);
                    // exculde the primery key and merge has columns which has edictor
                    if (record && currentRecord && tableStore) {
                        const cloneRecodData = record.clone().toData() || {};
                        const dealCloneRecodData = {};
                        const editeColumn = tableStore.columns
                            .filter((column) => !!column.editor)
                            .reduce((accumulator, nowValue) => [...accumulator, nowValue.name], [])
                            .filter((v) => !(v === null || v === undefined || v === ''));
                        if (editeColumn && editeColumn.length > 0) {
                            editeColumn.forEach(element => {
                                if (element) {
                                    dealCloneRecodData[element] = cloneRecodData[element];
                                }
                            });
                        }
                        // remove the unique name of fields
                        const uniqueFieldIterator = new Map([...dataSet.fields.entries()]
                            .filter(([_key, field]) => !!field.get('unique'))).keys();
                        const uniqueFieldNames = Array.from(uniqueFieldIterator);
                        if (uniqueFieldNames && uniqueFieldNames.length > 0) {
                            uniqueFieldNames.forEach(element => {
                                if (element) {
                                    delete dealCloneRecodData[element];
                                }
                            });
                        }
                        currentRecord.set(dealCloneRecodData);
                    }
                }
            }
        }
    }
    async handleKeyDownCTRLDELETE(e) {
        e.preventDefault();
        const { tableStore: { dataSet }, } = this;
        dataSet.delete(dataSet.selected);
    }
    async handleKeyDownUp(e) {
        e.preventDefault();
        const { currentRow } = this;
        let returnRecod;
        if (currentRow) {
            const previousElementSibling = findIndexedSibling(currentRow, -1);
            if (previousElementSibling) {
                returnRecod = this.focusRow(previousElementSibling);
            }
            else {
                const { dataSet } = this.props;
                await dataSet.prePage();
                returnRecod = this.focusRow(this.lastRow);
            }
            if (returnRecod) {
                return Promise.resolve(returnRecod);
            }
        }
        return Promise.reject();
    }
    async handleKeyDownDown(e) {
        e.preventDefault();
        const { currentRow } = this;
        let returnRecod;
        if (currentRow) {
            const nextElementSibling = findIndexedSibling(currentRow, 1);
            if (nextElementSibling) {
                returnRecod = this.focusRow(nextElementSibling);
            }
            else {
                const { dataSet } = this.props;
                await dataSet.nextPage();
                returnRecod = this.focusRow(this.firstRow);
            }
            if (returnRecod) {
                return Promise.resolve(returnRecod);
            }
        }
        return Promise.reject();
    }
    async handleKeyDownDownShift(e) {
        e.preventDefault();
        const { dataSet } = this.tableStore;
        const { currentRow } = this;
        if (currentRow && isCanEdictingRow(currentRow)) {
            const { index } = currentRow.dataset;
            if (index) {
                const record = dataSet.findRecordById(index);
                if (record) {
                    if (!record.selectable) {
                        this.handleKeyDownDown(e);
                    }
                    else if (!record.isSelected) {
                        dataSet.select(record);
                    }
                    else {
                        const currentRecord = await this.handleKeyDownDown(e);
                        if (currentRecord && dataSet) {
                            dataSet.select(currentRecord);
                        }
                    }
                }
            }
        }
        else {
            this.handleKeyDownDown(e);
        }
    }
    async handleKeyDownUpShift(e) {
        e.preventDefault();
        const { dataSet } = this.tableStore;
        const { currentRow } = this;
        if (currentRow && isCanEdictingRow(currentRow)) {
            const { index } = currentRow.dataset;
            if (index) {
                const record = dataSet.findRecordById(index);
                if (record) {
                    if (!record.selectable) {
                        this.handleKeyDownUp(e);
                    }
                    else if (!record.isSelected) {
                        dataSet.select(record);
                    }
                    else {
                        const currentRecord = await this.handleKeyDownUp(e);
                        if (currentRecord) {
                            dataSet.select(currentRecord);
                        }
                    }
                }
            }
        }
        else {
            this.handleKeyDownUp(e);
        }
    }
    handleKeyDownRight(e) {
        const { tableStore, props: { expandedRowRenderer, dataSet }, } = this;
        if (tableStore.isTree || expandedRowRenderer) {
            const { current } = dataSet;
            if (current) {
                e.preventDefault();
                tableStore.setRowExpanded(current, true);
            }
        }
    }
    handleKeyDownLeft(e) {
        const { tableStore, props: { expandedRowRenderer, dataSet }, } = this;
        if (tableStore.isTree || expandedRowRenderer) {
            const { current } = dataSet;
            if (current) {
                e.preventDefault();
                tableStore.setRowExpanded(current, false);
            }
        }
    }
    getOtherProps() {
        const otherProps = omit(super.getOtherProps(), [
            'columns',
            'header',
            'footer',
            'border',
            'style',
            'selectionMode',
            'alwaysShowRowBox',
            'onRow',
            'rowRenderer',
            'buttons',
            'rowHeight',
            'queryFields',
            'queryFieldsLimit',
            'summaryFieldsLimit',
            'queryBar',
            'autoFocus',
            'summaryBar',
            'defaultRowExpanded',
            'expandRowByClick',
            'expandedRowRenderer',
            'expandIconColumnIndex',
            'indentSize',
            'filter',
            'mode',
            'editMode',
            'filterBarFieldName',
            'filterBarPlaceholder',
            'pagination',
            'highLightRow',
            'selectedHighLightRow',
            'columnResizable',
            'columnTitleEditable',
            'pristine',
            'expandIcon',
            'spin',
            'virtual',
            'virtualCell',
            'virtualSpin',
            'autoHeight',
            'autoFootHeight',
            'useMouseBatchChoose',
            'autoMaxWidth',
            'dragColumnAlign',
            'columnDraggable',
            'rowDraggable',
            'onDragEnd',
            'rowDragRender',
            'columnsDragRender',
            'editorNextKeyEnterDown',
            'onDragEndBefore',
            'keyboard',
            'dynamicFilterBar',
            'parityRow',
            'rowNumber',
            'treeAsync',
            'treeLoadData',
            'customizable',
            'customizedCode',
            'dragColumn',
            'dragRow',
            'clientExportQuantity',
            'treeQueryExpanded',
        ]);
        otherProps.onKeyDown = this.handleKeyDown;
        const { rowHeight } = this.tableStore;
        if (rowHeight !== 'auto') {
            otherProps.style = { lineHeight: pxToRem(rowHeight) };
        }
        return otherProps;
    }
    getClassName() {
        const { tableStore: { prefixCls, border, rowHeight, parityRow }, } = this;
        return super.getClassName(`${prefixCls}-scroll-position-left`, {
            [`${prefixCls}-bordered`]: border,
            [`${prefixCls}-parity-row`]: parityRow,
            [`${prefixCls}-row-height-fixed`]: isNumber(rowHeight),
        });
    }
    getWrapperProps(props = {}) {
        const { style } = this.props;
        const { tableStore } = this;
        const newStyle = omit(style, ['width', 'height']);
        if (style && style.width !== undefined && style.width !== 'auto') {
            newStyle.width = Math.max(style.width, tableStore.leftLeafColumnsWidth + tableStore.rightLeafColumnsWidth + defaultMinWidth);
        }
        return super.getWrapperProps({
            style: newStyle,
            ...props,
        });
    }
    /**
     * 获取传入的 Spin props
     */
    getSpinProps() {
        const { spin, dataSet } = this.props;
        if (spin && !isUndefined(spin.spinning))
            return { ...spin };
        const { loading } = this.tableStore;
        if (loading) {
            return {
                ...spin,
                spinning: true,
            };
        }
        return {
            ...spin,
            dataSet,
        };
    }
    handleDragMouseUp() {
        const { dataSet, mouseBatchChooseIdList } = this.tableStore;
        if (this.tableStore.mouseBatchChooseState) {
            this.tableStore.mouseBatchChooseState = false;
            const { mouseBatchChooseStartId, mouseBatchChooseEndId } = this.tableStore;
            if (mouseBatchChooseStartId === mouseBatchChooseEndId) {
                return;
            }
            (mouseBatchChooseIdList || []).forEach((id) => {
                const record = dataSet.find((innerRecord) => innerRecord.id === id);
                if (record) {
                    dataSet.select(record);
                }
            });
        }
    }
    ;
    componentWillMount() {
        super.componentWillMount();
        this.initDefaultExpandedRows();
        this.connect();
    }
    componentDidMount() {
        this.syncSize();
        this.syncSizeInFrame();
    }
    componentWillReceiveProps(nextProps, nextContext) {
        super.componentWillReceiveProps(nextProps, nextContext);
        this.disconnect();
        this.tableStore.updateProps(nextProps);
        this.connect();
    }
    componentWillUnmount() {
        this.disconnect();
        if (this.scrollId !== undefined) {
            raf.cancel(this.scrollId);
        }
    }
    connect() {
        this.processDataSetListener(true);
        // 为什么使用 pointerup
        // 因为需要对disabled的元素进行特殊处理
        // 因为状态的改变依赖 mouseup 而在disabled的元素上 无法触发mouseup事件
        // 导致状态无法进行修正
        // 以下两种方案通过 pointer-events:none 进行处理
        // https://stackoverflow.com/questions/322378/javascript-check-if-mouse-button-down
        // https://stackoverflow.com/questions/62081666/the-event-of-the-document-is-not-triggered-when-it-is-on-a-disabled-element
        // 而使用指针事件可以突破disabled的限制
        // https://stackoverflow.com/questions/62126515/how-to-get-the-state-of-the-mouse-through-javascript/62127845#62127845
        if (this.tableStore.useMouseBatchChoose) {
            document.addEventListener('pointerup', this.handleDragMouseUp);
        }
        if (this.tableStore.heightType === TableHeightType.flex) {
            window.addEventListener('resize', this.handleWindowResize, false);
        }
    }
    disconnect() {
        this.processDataSetListener(false);
        if (this.tableStore.useMouseBatchChoose) {
            document.removeEventListener('pointerup', this.handleDragMouseUp);
        }
        window.removeEventListener('resize', this.handleWindowResize, false);
    }
    processDataSetListener(flag) {
        const { isTree, dataSet, inlineEdit } = this.tableStore;
        if (dataSet) {
            const handler = flag ? dataSet.addEventListener : dataSet.removeEventListener;
            if (isTree) {
                handler.call(dataSet, 'load', this.handleDataSetLoad);
            }
            if (inlineEdit) {
                handler.call(dataSet, 'create', this.handleDataSetCreate);
            }
        }
    }
    render() {
        const { tableStore, tableStore: { prefixCls, virtual, overflowX, overflowY, isAnyColumnsLeftLock, isAnyColumnsRightLock }, props: { style, treeQueryExpanded, spin, virtualSpin, buttons, queryFields, queryFieldsLimit, summaryFieldsLimit, filterBarFieldName, filterBarPlaceholder, summaryBar, dynamicFilterBar, clientExportQuantity, }, } = this;
        const content = this.getTable();
        const context = { tableStore };
        const pagination = this.getPagination(TablePaginationPosition.top);
        const tableSpinProps = getConfig('tableSpinProps');
        const styleHeight = style ? toPx(style.height) : 0;
        return (React.createElement(ReactResizeObserver, { resizeProp: "width", onResize: this.handleResize },
            React.createElement("div", Object.assign({}, this.getWrapperProps()),
                React.createElement(TableContext.Provider, { value: context },
                    React.createElement(ModalProvider, null,
                        this.getHeader(),
                        React.createElement(TableQueryBar, { buttons: buttons, pagination: pagination, queryFields: queryFields, clientExportQuantity: clientExportQuantity, summaryBar: summaryBar, dynamicFilterBar: dynamicFilterBar, queryFieldsLimit: queryFieldsLimit, summaryFieldsLimit: summaryFieldsLimit, filterBarFieldName: filterBarFieldName, filterBarPlaceholder: filterBarPlaceholder, treeQueryExpanded: treeQueryExpanded }),
                        React.createElement(Spin, Object.assign({}, tableSpinProps, this.getSpinProps(), { key: "content" }),
                            virtual && virtualSpin && (React.createElement("div", { ref: this.saveVirtualSpinRef, style: { display: 'none' } },
                                React.createElement(Spin, Object.assign({ key: "virtual", spinning: true, style: {
                                        height: pxToRem(styleHeight),
                                        lineHeight: pxToRem(styleHeight),
                                        position: 'absolute',
                                        width: '100%',
                                        zIndex: 4,
                                    } }, tableSpinProps, spin)))),
                            React.createElement("div", Object.assign({}, this.getOtherProps()),
                                React.createElement("div", { className: classNames(`${prefixCls}-content`, { [`${prefixCls}-content-overflow`]: isStickySupport() && overflowX && !overflowY }), onScroll: this.handleBodyScroll },
                                    !isStickySupport() && isAnyColumnsLeftLock && overflowX && this.getLeftFixedTable(),
                                    content,
                                    !isStickySupport() && isAnyColumnsRightLock && overflowX && this.getRightFixedTable()),
                                React.createElement("div", { ref: this.saveResizeRef, className: `${prefixCls}-split-line` }),
                                this.getFooter())),
                        this.getPagination(TablePaginationPosition.bottom))))));
    }
    reorderDataSet(startIndex, endIndex) {
        const { dataSet } = this.tableStore;
        dataSet.move(startIndex, endIndex);
    }
    ;
    handleDragEnd(resultDrag, provided) {
        const { onDragEnd, onDragEndBefore } = this.props;
        let resultBefore = resultDrag;
        if (onDragEndBefore) {
            const result = onDragEndBefore(this.tableStore.dataSet, toJS(this.tableStore.columns), resultDrag, provided);
            if (result === false) {
                return;
            }
            if (isDropresult(result)) {
                resultBefore = result;
            }
        }
        if (resultBefore && resultBefore.destination) {
            this.reorderDataSet(resultBefore.source.index, resultBefore.destination.index);
        }
        /**
         * 相应变化后的数据
         */
        if (onDragEnd) {
            onDragEnd(this.tableStore.dataSet, toJS(this.tableStore.columns), resultBefore, provided);
        }
    }
    handleBodyScroll(e) {
        const { currentTarget } = e;
        const handle = () => {
            this.handleBodyScrollTop(e, currentTarget);
            this.handleBodyScrollLeft(e, currentTarget);
        };
        if (isStickySupport()) {
            handle();
        }
        else {
            e.persist();
            this.scrollId = raf(handle);
        }
    }
    handleBodyScrollTop(e, currentTarget) {
        const { target } = e;
        const { tableStore: { virtual, heightType, lastScrollTop }, } = this;
        if ((isStickySupport() && !virtual) ||
            (![TableHeightType.fixed, TableHeightType.flex].includes(heightType)) ||
            currentTarget !== target ||
            target === this.tableFootWrap) {
            return;
        }
        const fixedColumnsBodyLeft = this.fixedColumnsBodyLeft;
        const bodyTable = this.tableBodyWrap;
        const fixedColumnsBodyRight = this.fixedColumnsBodyRight;
        const { scrollTop } = target;
        if (scrollTop !== lastScrollTop) {
            if (fixedColumnsBodyLeft && target !== fixedColumnsBodyLeft) {
                fixedColumnsBodyLeft.scrollTop = scrollTop;
            }
            if (bodyTable && target !== bodyTable) {
                bodyTable.scrollTop = scrollTop;
            }
            if (fixedColumnsBodyRight && target !== fixedColumnsBodyRight) {
                fixedColumnsBodyRight.scrollTop = scrollTop;
            }
            const { refSpin } = this;
            if (refSpin) {
                refSpin.style.display = 'block';
                this.setSpin();
            }
            this.tableStore.setLastScrollTop(scrollTop);
        }
    }
    handleBodyScrollLeft(e, currentTarget) {
        const { target } = e;
        const { tableStore } = this;
        const headTable = this.tableHeadWrap;
        const bodyTable = this.tableBodyWrap;
        const footTable = this.tableFootWrap;
        if (!tableStore.overflowX ||
            currentTarget !== target ||
            target === this.fixedColumnsBodyRight ||
            target === this.fixedColumnsBodyLeft) {
            return;
        }
        const { scrollLeft } = target;
        if (scrollLeft !== this.lastScrollLeft) {
            if (isStickySupport()) {
                [...tableStore.editors.values()].forEach((editor) => {
                    if (editor.lock && editor.cellNode) {
                        if (tableStore.inlineEdit) {
                            editor.alignEditor(editor.cellNode);
                        }
                        else {
                            editor.hideEditor();
                        }
                    }
                });
            }
            if (headTable && target !== headTable) {
                headTable.scrollLeft = scrollLeft;
            }
            if (bodyTable && target !== bodyTable) {
                bodyTable.scrollLeft = scrollLeft;
            }
            if (footTable && target !== footTable) {
                footTable.scrollLeft = scrollLeft;
            }
            this.setScrollPositionClassName(target);
        }
        this.lastScrollLeft = scrollLeft;
    }
    setScrollPositionClassName(target) {
        if (this.tableStore.isAnyColumnsLock) {
            const node = target || this.tableBodyWrap;
            if (node) {
                const scrollToLeft = node.scrollLeft === 0;
                const table = node.querySelector('table');
                const scrollToRight = table && node.scrollLeft >= table.offsetWidth - node.offsetWidth;
                if (scrollToLeft && scrollToRight) {
                    this.setScrollPosition(ScrollPosition.both);
                }
                else if (scrollToLeft) {
                    this.setScrollPosition(ScrollPosition.left);
                }
                else if (scrollToRight) {
                    this.setScrollPosition(ScrollPosition.right);
                }
                else {
                    this.setScrollPosition(ScrollPosition.middle);
                }
            }
        }
    }
    setScrollPosition(position) {
        if (this.scrollPosition !== position) {
            this.scrollPosition = position;
            const { prefixCls } = this.tableStore;
            const cls = classes(this.element).remove(new RegExp(`^${prefixCls}-scroll-position-.+$`));
            if (position === ScrollPosition.both) {
                cls.add(`${prefixCls}-scroll-position-left`).add(`${prefixCls}-scroll-position-right`);
            }
            else {
                cls.add(`${prefixCls}-scroll-position-${position}`);
            }
        }
    }
    renderTable(hasHeader, hasBody, hasFooter, lock) {
        const { tableStore: { virtual }, } = this;
        return virtual ? (React.createElement(React.Fragment, null,
            hasHeader && (React.createElement(TableWrapper, { key: "tableWrapper-header", lock: lock, hasBody: hasBody, hasHeader: hasHeader, hasFooter: hasFooter }, this.getTableHeader(lock))),
            hasBody && (React.createElement(VirtualWrapper, null,
                React.createElement(TableWrapper, { key: "tableWrapper-body", lock: lock, hasBody: hasBody, hasHeader: hasHeader, hasFooter: hasFooter }, this.getTableBody(lock)))),
            hasFooter && (React.createElement(TableWrapper, { key: "tableWrapper-footer", lock: lock, hasBody: hasBody, hasHeader: hasFooter, hasFooter: hasFooter }, this.getTableFooter(lock))))) : (React.createElement(TableWrapper, { key: "tableWrapper", lock: lock, hasBody: hasBody, hasHeader: hasHeader, hasFooter: hasFooter },
            hasHeader && this.getTableHeader(lock),
            hasBody && this.getTableBody(lock),
            hasFooter && this.getTableFooter(lock)));
    }
    getHeader() {
        const { props: { header, dataSet }, } = this;
        if (header) {
            const { prefixCls } = this.tableStore;
            const data = dataSet ? dataSet.records : [];
            return (React.createElement("div", { key: "header", className: `${prefixCls}-header` }, typeof header === 'function' ? header(data) : header));
        }
    }
    getFooter() {
        const { props: { footer, dataSet }, } = this;
        if (footer) {
            const { prefixCls } = this.tableStore;
            const data = dataSet ? dataSet.records : [];
            return (React.createElement("div", { key: "footer", className: `${prefixCls}-footer` }, typeof footer === 'function' ? footer(data) : footer));
        }
    }
    getPagination(position) {
        const { props: { dataSet, selectionMode }, tableStore: { prefixCls, pagination, showSelectionTips }, } = this;
        if (pagination !== false && dataSet && dataSet.paging) {
            const paginationPosition = getPaginationPosition(pagination);
            if (paginationPosition === TablePaginationPosition.both || paginationPosition === position) {
                const paginationProps = omit(pagination, 'position');
                return (React.createElement(Pagination, Object.assign({ key: `pagination-${position}` }, paginationProps, { className: classNames(`${prefixCls}-pagination`, paginationProps.className, { [`${prefixCls}-pagination-with-selection-tips`]: showSelectionTips }), dataSet: dataSet }), selectionMode !== SelectionMode.none && dataSet.selection === DataSetSelection.multiple && React.createElement(SelectionTips, null)));
            }
        }
    }
    getTable(lock) {
        const { overflowX, heightType, hasFooter: footer } = this.tableStore;
        let tableHead;
        let tableBody;
        let tableFooter;
        if ((!isStickySupport() && overflowX) || [TableHeightType.flex, TableHeightType.fixed].includes(heightType)) {
            const { prefixCls, leftLeafColumnsWidth, rightLeafColumnsWidth, overflowY } = this.tableStore;
            let tableHeadRef;
            let tableBodyRef;
            let tableFootRef;
            if (!lock) {
                tableHeadRef = node => (this.tableHeadWrap = node);
                tableFootRef = node => (this.tableFootWrap = node);
                tableBodyRef = node => (this.tableBodyWrap = node);
            }
            else if (lock === ColumnLock.right) {
                tableBodyRef = (node) => {
                    this.fixedColumnsBodyRight = node;
                    if (node) {
                        this.syncSize();
                    }
                };
            }
            else {
                tableBodyRef = (node) => {
                    this.fixedColumnsBodyLeft = node;
                    if (node) {
                        this.syncSize();
                    }
                };
            }
            const style = lock ? {
                width: pxToRem(lock === ColumnLock.right
                    ? (rightLeafColumnsWidth - 1 + (overflowY ? measureScrollbar() : 0))
                    : leftLeafColumnsWidth),
                marginLeft: lock === ColumnLock.right ? pxToRem(1) : undefined,
            } : undefined;
            tableHead = (React.createElement("div", { key: "tableHead", ref: tableHeadRef, className: `${prefixCls}-head`, style: style }, this.renderTable(true, false, false, lock)));
            tableBody = (React.createElement(TableBody, { key: "tableBody", getRef: tableBodyRef, lock: lock, onScroll: this.handleBodyScroll }, this.renderTable(false, true, false, lock)));
            if (footer) {
                tableFooter = (React.createElement("div", { key: "tableFooter", ref: tableFootRef, className: `${prefixCls}-foot`, style: style, onScroll: this.handleBodyScroll }, this.renderTable(false, false, true, lock)));
            }
        }
        else {
            tableBody = this.renderTable(true, true, footer, lock);
        }
        return [tableHead, tableBody, tableFooter];
    }
    getLeftFixedTable() {
        const { prefixCls } = this.tableStore;
        const table = this.getTable(ColumnLock.left);
        return React.createElement("div", { className: `${prefixCls}-fixed-left` }, table);
    }
    getRightFixedTable() {
        const { prefixCls } = this.tableStore;
        const table = this.getTable(ColumnLock.right);
        return React.createElement("div", { className: `${prefixCls}-fixed-right` }, table);
    }
    getTableBody(lock) {
        const { tableStore: { rowDraggable } } = this;
        const body = React.createElement(TableTBody, { key: "tbody", lock: lock });
        return rowDraggable ? (React.createElement(DragDropContext, { onDragEnd: this.handleDragEnd }, body)) : body;
    }
    getTableHeader(lock) {
        const { props: { dataSet } } = this;
        return (React.createElement(TableHeader, { key: "thead", ref: lock ? undefined : this.saveTableHeader, lock: lock, dataSet: dataSet }));
    }
    getTableFooter(lock) {
        const { props: { dataSet }, } = this;
        return React.createElement(TableFooter, { key: "tfoot", ref: lock ? undefined : this.saveTableFooter, lock: lock, dataSet: dataSet });
    }
    getStyleHeight() {
        const { style } = this.props;
        if (style) {
            return toPx(style.height);
        }
    }
    syncSizeInFrame(width = this.getWidth()) {
        if (this.nextFrameActionId !== undefined) {
            raf.cancel(this.nextFrameActionId);
        }
        this.nextFrameActionId = raf(this.syncSize.bind(this, width));
    }
    getContentHeight() {
        const { wrapper, element, tableBodyWrap, tableStore: { prefixCls, autoHeight, customized: { heightType, height, heightDiff }, tempCustomized, }, } = this;
        const tempHeightType = get(tempCustomized, 'heightType');
        if (tempHeightType) {
            if (tempHeightType === TableHeightType.fixed) {
                return get(tempCustomized, 'height');
            }
            if (tempHeightType === TableHeightType.flex) {
                return document.documentElement.clientHeight - (get(tempCustomized, 'heightDiff') || 0);
            }
            return undefined;
        }
        if (heightType) {
            if (heightType === TableHeightType.fixed) {
                return height;
            }
            if (heightType === TableHeightType.flex) {
                return document.documentElement.clientHeight - (heightDiff || 0);
            }
            return undefined;
        }
        if (autoHeight) {
            const { top: parentTop, height: parentHeight } = wrapper.parentNode.getBoundingClientRect();
            const { paddingBottom } = document.defaultView ? document.defaultView.getComputedStyle(wrapper.parentNode) : { paddingBottom: 0 };
            const { top: tableTop } = element.getBoundingClientRect();
            const { diff, type } = autoHeight;
            const paddingBottomPx = toPx(paddingBottom) || 0;
            if (wrapper) {
                if (type === TableAutoHeightType.minHeight) {
                    return parentHeight - (tableTop - parentTop) - diff - paddingBottomPx;
                }
                const maxHeight = parentHeight - (tableTop - parentTop) - diff - paddingBottomPx;
                // 保证max高度和Height维持一致防止scroll问题 maxHeight - 外框paddingBottom 以及 diff 和其他 tableBody 以外的高度。
                if (tableBodyWrap) {
                    let maxBodyHeight = maxHeight;
                    const tableHeader = element.querySelector(`.${prefixCls}-thead`);
                    const tableFooter = element.querySelector(`.${prefixCls}-footer`);
                    const tableFootWrap = element.querySelector(`.${prefixCls}-foot`);
                    if (tableHeader) {
                        maxBodyHeight -= getHeight(tableHeader);
                    }
                    if (tableFooter) {
                        maxBodyHeight -= getHeight(tableFooter);
                    }
                    if (tableFootWrap) {
                        maxBodyHeight -= getHeight(tableFootWrap);
                    }
                    tableBodyWrap.style.maxHeight = pxToRem(maxBodyHeight) || '';
                }
                return maxHeight || 0;
            }
        }
        return this.getStyleHeight();
    }
    ;
    syncSize(width = this.getWidth()) {
        const { element, tableStore } = this;
        if (element) {
            tableStore.width = Math.floor(width);
            const height = this.getContentHeight();
            if (isNumber(height)) {
                const { rowHeight, lockColumnsBodyRowsHeight } = tableStore;
                const { tableHeader, tableFooter } = this;
                const headerHeight = tableHeader ? tableHeader.getHeight() : 0;
                const footerHeight = tableFooter ? tableFooter.getHeight() : 0;
                const totalHeight = Math.max(height, (isNumber(rowHeight) ? rowHeight : lockColumnsBodyRowsHeight[0] || 0) + headerHeight + footerHeight);
                tableStore.totalHeight = totalHeight;
                tableStore.height = totalHeight - headerHeight - footerHeight;
            }
            else {
                tableStore.totalHeight = element.offsetHeight;
                tableStore.height = undefined;
            }
        }
        this.setScrollPositionClassName();
    }
    initDefaultExpandedRows() {
        const { tableStore, props: { dataSet, defaultRowExpanded }, } = this;
        if (tableStore.isTree && defaultRowExpanded) {
            dataSet.forEach((record) => {
                if (record.children) {
                    record.isExpanded = true;
                }
            });
        }
    }
    handleHeightTypeChange(immediate) {
        this.disconnect();
        if (immediate) {
            this.syncSize();
        }
        else {
            this.syncSizeInFrame();
        }
        this.connect();
    }
    getWidth() {
        const { wrapper } = this;
        if (wrapper) {
            return Math.floor(wrapper.getBoundingClientRect().width);
        }
        return 0;
    }
};
Table.displayName = 'Table';
Table.Column = Column;
Table.FilterBar = FilterBar;
Table.AdvancedQueryBar = AdvancedQueryBar;
Table.ProfessionalBar = ProfessionalBar;
Table.DynamicFilterBar = DynamicFilterBar;
Table.ToolBar = ToolBar;
Table.TableRow = TableRow;
Table.TableHeaderCell = TableHeaderCell;
Table.propTypes = {
    columns: PropTypes.array,
    /**
     * 表头
     */
    header: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /**
     * 表脚
     */
    footer: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /**
     * 是否显示边框
     * @default true
     */
    border: PropTypes.bool,
    /**
     * 功能按钮
     * 可选值：`add` `delete` `remove` `save` `query` `expandAll` `collapseAll` 或 自定义按钮
     */
    buttons: PropTypes.arrayOf(PropTypes.oneOfType([
        buttonsEnumType,
        PropTypes.arrayOf(PropTypes.oneOfType([buttonsEnumType, PropTypes.object])),
        PropTypes.node,
    ])),
    /**
     * 自定义查询字段组件
     * 默认会根据queryDataSet中定义的field类型自动匹配组件， 匹配类型如下
     * lovCode => Lov
     * lookupCode => Select
     * type:number => NumberField
     * type:date => DatePicker
     * type:dateTime => DatePicker[mode=dateTime]
     * type:week => DatePicker[mode=week]
     * default => TextField
     */
    queryFields: PropTypes.object,
    /**
     * 头部显示的查询字段的数量，超出限制的查询字段放入弹出窗口
     * @default 1
     */
    queryFieldsLimit: PropTypes.number,
    /**
     * 头部显示的汇总字段的数量，超出限制的查询字段放入弹出窗口
     * @default 3
     */
    summaryFieldsLimit: PropTypes.number,
    /**
     * 显示查询条
     * @default true
     */
    queryBar: PropTypes.oneOfType([
        PropTypes.oneOf([
            TableQueryBarType.advancedBar,
            TableQueryBarType.normal,
            TableQueryBarType.bar,
            TableQueryBarType.none,
            TableQueryBarType.professionalBar,
            TableQueryBarType.filterBar,
        ]),
        PropTypes.func,
    ]),
    useMouseBatchChoose: PropTypes.bool,
    /**
     * 行高
     * @default 30
     */
    rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
    alwaysShowRowBox: PropTypes.bool,
    defaultRowExpanded: PropTypes.bool,
    expandRowByClick: PropTypes.bool,
    indentSize: PropTypes.number,
    filter: PropTypes.func,
    mode: PropTypes.oneOf([TableMode.list, TableMode.tree]),
    editMode: PropTypes.oneOf([TableEditMode.inline, TableEditMode.cell]),
    filterBarFieldName: PropTypes.string,
    filterBarPlaceholder: PropTypes.string,
    highLightRow: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([HighLightRowType.focus, HighLightRowType.click])]),
    selectedHighLightRow: PropTypes.bool,
    parityRow: PropTypes.bool,
    autoMaxWidth: PropTypes.bool,
    /**
     * 设置drag框体位置
     */
    dragColumnAlign: PropTypes.oneOf([DragColumnAlign.left, DragColumnAlign.right]),
    /**
     * 开启列拖拽，但是无法使用宽度拖拽
     */
    columnDraggable: PropTypes.bool,
    /**
     * 开启行拖拽
     */
    rowDraggable: PropTypes.bool,
    columnsDragRender: PropTypes.object,
    expandIcon: PropTypes.func,
    rowDragRender: PropTypes.object,
    onDragEndBefore: PropTypes.func,
    /**
     * 开启新建自动定位
     */
    autoFocus: PropTypes.bool,
    /**
     * 新增的组合键开关闭
     */
    keyboard: PropTypes.bool,
    /**
     * 是否单独处理column footer
     */
    autoFootHeight: PropTypes.bool,
    /**
     * 客户端查询导出，查询数目设置
     */
    clientExportQuantity: PropTypes.number,
    /**
     * 可以修改由于样式导致的虚拟高度和rowHeight不一致
     */
    virtualRowHeight: PropTypes.number,
    ...DataSetComponent.propTypes,
};
Table.defaultProps = {
    suffixCls: 'table',
    tabIndex: 0,
    selectionMode: SelectionMode.rowbox,
    queryFields: {},
    defaultRowExpanded: false,
    expandRowByClick: false,
    indentSize: 15,
    summaryFieldsLimit: 3,
    filterBarFieldName: 'params',
    virtual: false,
    virtualSpin: false,
    autoHeight: false,
    autoMaxWidth: true,
    autoFootHeight: false,
    clientExportQuantity: 100,
};
__decorate([
    autobind
], Table.prototype, "saveVirtualSpinRef", null);
__decorate([
    autobind
], Table.prototype, "saveResizeRef", null);
__decorate([
    autobind
], Table.prototype, "saveTableHeader", null);
__decorate([
    autobind
], Table.prototype, "saveTableFooter", null);
__decorate([
    autobind,
    action
], Table.prototype, "handleResize", null);
__decorate([
    autobind
], Table.prototype, "handleWindowResize", null);
__decorate([
    autobind
], Table.prototype, "handleDataSetLoad", null);
__decorate([
    autobind
], Table.prototype, "handleDataSetCreate", null);
__decorate([
    autobind
], Table.prototype, "handleKeyDown", null);
__decorate([
    autobind
], Table.prototype, "handleDragMouseUp", null);
__decorate([
    action
], Table.prototype, "reorderDataSet", null);
__decorate([
    autobind
], Table.prototype, "handleDragEnd", null);
__decorate([
    autobind
], Table.prototype, "handleBodyScroll", null);
__decorate([
    autobind,
    action
], Table.prototype, "syncSize", null);
__decorate([
    action
], Table.prototype, "initDefaultExpandedRows", null);
Table = __decorate([
    observer
], Table);
export default Table;
